/**
 * Badge Component
 *
 * Categorical/decorative label badges with caller-specified colors.
 * Use for category tags, type labels, priority indicators, etc.
 *
 * For semantic workflow states (Active, Pending, Cancelled),
 * use StatusBadge instead.
 *
 * For notification counters (unread counts, alert dots),
 * use NotificationBadge instead.
 *
 * SCRUM-901: StatusBadge & Badge Component Extraction
 *
 * @component
 * @param {Object} props
 * @param {string} props.color - Tailwind color name (blue, green, red, yellow, gray, purple, indigo, pink, orange, amber)
 * @param {React.ReactNode} props.children - Badge label text
 * @param {string} [props.className] - Additional classes
 */
import PropTypes from 'prop-types';

const colorClasses = {
  blue: 'bg-blue-100 text-blue-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  gray: 'bg-gray-100 text-gray-800',
  purple: 'bg-purple-100 text-purple-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  pink: 'bg-pink-100 text-pink-800',
  orange: 'bg-orange-100 text-orange-800',
  amber: 'bg-amber-100 text-amber-800',
};

export default function Badge({ color = 'gray', children, className = '' }) {
  const classes = colorClasses[color] || colorClasses.gray;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${classes} ${className}`.trim()}
    >
      {children}
    </span>
  );
}

Badge.propTypes = {
  color: PropTypes.oneOf(Object.keys(colorClasses)),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};
